import { Form } from "antd"
import dayjs from "dayjs"
import { isEmpty, keyBy } from "lodash"
import { useMemo } from "react"

import { classAtoms, classSessionAtoms, makeupAtoms, tryoutAtoms } from "#atoms"
import { CLASS_GROUPS } from "#constants/classOptions"
import {
  ATTENDANCE_STATUSES,
  TIME_BLOCKING_ATTENDANCE_STATUSES,
} from "#constants/globalConstants"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { MakeupConstructor } from "#customTypes/composedTypes/makeupTypes"
import { TryoutConstructor } from "#customTypes/composedTypes/tryoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import { Attendance } from "#customTypes/types"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import { useFilteredDocs, useSomeDocs } from "#hooks/jotai/useSubscription"
import { getSessionHeadcount, getSessionInfoFromId } from "#utils/classHelpers"
import { getNow } from "#utils/dateHelpers"

const NULL_QUERY_CONFIG: FirebaseQueryConfig = {
  queries: {
    id: { value: "__NULL__", comparator: COMPARATORS.EQUALS },
  },
}

const useTryoutSessionOptions = (selectedSessionId: Uuid | undefined) => {
  const form = Form.useFormInstance()
  const studentId: Uuid | null = Form.useWatch("studentId", form) ?? null
  const tryoutFilterDate = Form.useWatch("tryoutFilterDate", form)
  const semester: string | undefined = Form.useWatch(["semester"], form)
  const recommendedClassGroup: CLASS_GROUPS | undefined = Form.useWatch(
    ["step2", "recommendedClassGroup"],
    form
  )
  const recommendedTeacherId: Uuid | undefined = Form.useWatch(
    ["step2", "recommendedTeacherId"],
    form
  )

  const student = useGetOneStudent(studentId ?? undefined)

  const classQueryConfig: FirebaseQueryConfig = useMemo(() => {
    if (!semester) return NULL_QUERY_CONFIG

    const queries: FirebaseQueryConfig["queries"] = {}

    if (semester) {
      queries.semester = {
        value: semester,
        comparator: COMPARATORS.EQUALS,
      }
    }
    if (recommendedClassGroup) {
      queries.group = {
        value: recommendedClassGroup,
        comparator: COMPARATORS.EQUALS,
      }
    }

    return { queries }
  }, [semester, recommendedClassGroup])

  const possibleTryoutClasses = useFilteredDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    classQueryConfig
  )

  const possibleTryoutClassesById = useMemo(
    () => keyBy(possibleTryoutClasses, "id"),
    [possibleTryoutClasses]
  )

  // Build session query (date window + optional teacher)
  const sessionQueryConfig: FirebaseQueryConfig = useMemo(() => {
    if (!tryoutFilterDate && !recommendedTeacherId) return NULL_QUERY_CONFIG

    const queries: FirebaseQueryConfig["queries"] = {}

    if (tryoutFilterDate) {
      queries!.eventStart = {
        value: [
          dayjs(tryoutFilterDate).startOf("day").toISOString(),
          dayjs(tryoutFilterDate).add(2, "week").endOf("day").toISOString(),
        ],
        comparator: COMPARATORS.BETWEEN,
      }
    }
    if (recommendedTeacherId) {
      queries!.teacherIds = {
        value: recommendedTeacherId,
        comparator: COMPARATORS.CONTAINS,
      }
    }
    return { queries }
  }, [tryoutFilterDate, recommendedTeacherId])

  const candidateSessions = useFilteredDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    sessionQueryConfig
  )

  const possibleTryoutClassSessions = useMemo(() => {
    if (!candidateSessions?.length) return []

    return candidateSessions.filter(
      (item) => !!possibleTryoutClassesById[item.classId]
    )
  }, [candidateSessions, possibleTryoutClassesById])

  // Student classes and sessions for conflict checks
  const studentClassIds = useMemo(
    () => Object.keys(student?.attendance ?? {}),
    [student]
  )

  const studentClasses = useSomeDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    studentClassIds
  )

  const studentClassSessionIds = useMemo(
    () =>
      studentClasses
        .filter((c) => c.semester === semester)
        .flatMap((c) => c.sessionIds),
    [studentClasses, semester]
  )

  const studentClassSessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    studentClassSessionIds
  )

  // Student tryouts to avoid duplicates
  const studentTryoutsQueryConfig: FirebaseQueryConfig = useMemo(() => {
    if (!studentId) return NULL_QUERY_CONFIG

    return {
      queries: {
        studentId: { value: studentId, comparator: COMPARATORS.EQUALS },
      },
    }
  }, [studentId])

  const studentTryouts = useFilteredDocs<TryoutConstructor>(
    tryoutAtoms,
    DOC_TYPES.TRYOUTS,
    studentTryoutsQueryConfig
  )

  // Student makeups for time-conflict checks
  const studentMakeupsQueryConfig: FirebaseQueryConfig = useMemo(() => {
    if (!studentId || !semester) return NULL_QUERY_CONFIG

    return {
      queries: {
        studentId: { value: studentId, comparator: COMPARATORS.EQUALS },
        semester: { value: semester, comparator: COMPARATORS.EQUALS },
      },
    }
  }, [studentId, semester])

  const studentMakeups = useFilteredDocs<MakeupConstructor>(
    makeupAtoms,
    DOC_TYPES.MAKEUPS,
    studentMakeupsQueryConfig
  )

  const makeupSessionIds = useMemo(
    () =>
      studentMakeups
        ?.filter((m) => !!m.makeupSessionId)
        .map((m) => m.makeupSessionId!) ?? [],
    [studentMakeups]
  )

  const makeupSessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    makeupSessionIds
  )

  const tryoutSessionOptions = useMemo(
    () =>
      buildTryoutSessionOptions({
        studentAttendance: student?.attendance,
        studentTryouts,
        studentClassSessions,
        makeupSessions,
        possibleTryoutClasses,
        possibleTryoutClassSessions,
        selectedSessionId,
      }),
    [
      student,
      studentTryouts,
      studentClassSessions,
      makeupSessions,
      possibleTryoutClasses,
      possibleTryoutClassSessions,
      selectedSessionId,
    ]
  )

  return tryoutSessionOptions
}

export type TryoutSessionOption = {
  value: string
  label: string
  disabled: boolean
  hasConflict: boolean
  hasWarning: boolean
}

export const buildTryoutSessionOptions = ({
  studentAttendance,
  studentTryouts,
  studentClassSessions,
  makeupSessions,
  possibleTryoutClasses,
  possibleTryoutClassSessions,
  selectedSessionId,
}: {
  studentAttendance: Attendance | undefined | null
  studentTryouts: TryoutConstructor[] | undefined | null
  studentClassSessions: ClassSessionConstructor[]
  makeupSessions: ClassSessionConstructor[]
  possibleTryoutClasses: ClassConstructor[] | undefined | null
  possibleTryoutClassSessions: ClassSessionConstructor[]
  selectedSessionId: Uuid | undefined
}): TryoutSessionOption[] => {
  try {
    if (isEmpty(possibleTryoutClassSessions)) {
      return []
    }

    const classesById = keyBy(possibleTryoutClasses, "id")
    const sessionsById = keyBy(possibleTryoutClassSessions, "id")

    const sortedSessions = Object.values(sessionsById).sort((a, b) =>
      a.eventStart.localeCompare(b.eventStart)
    )

    const results: TryoutSessionOption[] = []

    sortedSessions.forEach(({ id, eventStart, eventEnd, classId }) => {
      let reasonForInvalid = ""
      let warningReason = ""

      const isAlreadyScheduledInThisClass = (
        studentAttendance?.[classId] ?? []
      ).some(
        ({ status }) => !!status && status !== ATTENDANCE_STATUSES.NOT_ENROLLED
      )

      if (isAlreadyScheduledInThisClass) {
        reasonForInvalid = "Already in this class"
      }

      if (dayjs(eventStart).isBefore(getNow())) {
        warningReason = "Session is in the past"
      }

      const isAlreadySignedUpForTryout = studentTryouts?.find(
        (t) => t.sessionId === id
      )
      if (isAlreadySignedUpForTryout) {
        reasonForInvalid = "Tryout already scheduled"
      }

      const isConflictingClassTime = studentClassSessions
        .filter((s) => s.id !== selectedSessionId)
        .some((studentClassSession) => {
          const [studentClassId, studentSessionIndex] = getSessionInfoFromId(
            studentClassSession.id
          )
          const studentClassAttendanceStatus =
            studentAttendance?.[studentClassId]?.[studentSessionIndex]?.status

          if (
            studentClassId === classId ||
            !studentClassAttendanceStatus ||
            !TIME_BLOCKING_ATTENDANCE_STATUSES[studentClassAttendanceStatus]
          ) {
            return false
          }

          return (
            dayjs(studentClassSession.eventStart).isBefore(eventEnd) &&
            dayjs(eventStart).isBefore(studentClassSession.eventEnd)
          )
        })
      if (isConflictingClassTime) {
        reasonForInvalid = "Time conflict"
      }

      const isConflictingMakeupTime = makeupSessions.some(
        (makeupSession) =>
          dayjs(makeupSession.eventStart).isBefore(eventEnd) &&
          dayjs(eventStart).isBefore(makeupSession.eventEnd)
      )
      if (isConflictingMakeupTime) {
        reasonForInvalid = "Makeup scheduled"
      }

      if (!classesById[classId]?.isTryoutAllowed) {
        reasonForInvalid = "Tryout not allowed"
      }

      const isCurrentSelection = !!selectedSessionId && id === selectedSessionId
      const hasConflict = !isCurrentSelection && reasonForInvalid.length > 0
      const hasWarning = !isCurrentSelection && !hasConflict && !!warningReason
      const annotation = isCurrentSelection
        ? " - Current selection"
        : hasConflict
          ? ` (${reasonForInvalid})`
          : hasWarning
            ? ` (${warningReason})`
            : ""

      results.push({
        value: id,
        disabled: hasConflict,
        hasConflict,
        hasWarning,
        label: `${classesById[classId]?.name} - ${dayjs(eventStart).format(
          "M/D/YY h:mm a"
        )} - ${getSessionHeadcount(sessionsById[id])}/${
          classesById[classId]?.capacity
        }${annotation}`,
      })
    })

    return results
  } catch {
    return []
  }
}

export default useTryoutSessionOptions
